import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from 'src/shared/components/ui/sheet';
import { Input } from 'src/shared/components/ui/input';
import { Button } from 'src/shared/components/ui/button';
import { useUpdateCustomerMutation } from 'src/app/admin/state/api/adminGraphSlice';

type ManageOrganisationSheetProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    customerId: string;
    customerName: string;
};

/** Where an organisation's editable details are changed. Today that is its name alone. */
const ManageOrganisationSheet = ({ open, onOpenChange, customerId, customerName }: ManageOrganisationSheetProps) => {
    const [updateCustomer, { isLoading }] = useUpdateCustomerMutation();
    const [name, setName] = useState(customerName);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        setName(customerName);
    }, [customerName]);

    const trimmedName = name.trim();
    // A change that changes nothing is not worth sending to a super-admin-only mutation.
    const canSubmit = trimmedName !== '' && trimmedName !== customerName && !isLoading;

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            setName(customerName);
            setErrorMessage(null);
        }
        onOpenChange(nextOpen);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSubmit) {
            return;
        }
        setErrorMessage(null);
        try {
            await updateCustomer({ customerId, customerName: trimmedName }).unwrap();
            onOpenChange(false);
        } catch {
            setErrorMessage('Something went wrong renaming the organisation. Please try again.');
        }
    };

    return (
        <Sheet open={open} onOpenChange={handleOpenChange}>
            <SheetContent>
                <SheetHeader>
                    <SheetTitle>Rename organisation</SheetTitle>
                    <SheetDescription>Change the display name for {customerId}. The Customer ID cannot be changed.</SheetDescription>
                </SheetHeader>

                <form onSubmit={handleSubmit} className='flex flex-col gap-4 mt-6' data-testid='manage-organisation-form'>
                    <div className='flex flex-col gap-1'>
                        <label htmlFor='organisation-name' className='text-sm font-medium'>
                            Organisation name
                        </label>
                        <Input id='organisation-name' value={name} onChange={event => setName(event.target.value)} />
                    </div>

                    {errorMessage && (
                        <p role='alert' className='text-sm text-red-100'>
                            {errorMessage}
                        </p>
                    )}

                    <SheetFooter className='mt-2'>
                        <Button type='button' variant='outline' onClick={() => handleOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type='submit' disabled={!canSubmit} className='flex-grow'>
                            Save
                        </Button>
                    </SheetFooter>
                </form>
            </SheetContent>
        </Sheet>
    );
};

export default ManageOrganisationSheet;
